

interface AddressOption {
  label: string;
  value: string;
  description?: string;
  placeId?: string;
  isPostcodeData?: boolean;
  postcodeData?: any;
  structured_formatting?: any;
}

import { config } from '@/config/config';
import { fetchAddressSuggestions } from '../addressApis';

interface TrimAddressParams {
  mainAddress: string;
  secondryAddress?: string;
}

export const trimAddress = ({ mainAddress, secondryAddress }: TrimAddressParams): string => {
  if (!secondryAddress) {
    return mainAddress
      .replace(/Street /gi, 'St ')
      .replace(/Court /gi, 'Crt ')
      .replace(`, ${config.store.country}`, '')
      .replace(`, ${config.store.countryAbv}`, '')
      .replace(`${config.store.state}`, '')
      .replace(`${config.store.stateIdentifier}`, '');
  } else {
    const main = mainAddress
      .replace(/Street /gi, 'St ')
      .replace(/Court /gi, 'Crt ');
    const secondry = secondryAddress
      .replace(/Street /gi, 'St ')
      .replace(/Court /gi, 'Crt ')
      .replace(`, ${config.store.city}`, '')
      .replace(`, ${config.store.country}`, '')
      .replace(`, ${config.store.countryAbv}`, '')
      .replace(`${config.store.state}`, '')
      .replace(`${config.store.stateIdentifier}`, '');

    return `${main}${secondry ? `, ${secondry}` : ''}`;
  }
};

export const mapPostcodeDataToOptions = (
  postcodeData: any
): AddressOption[] => {
  return postcodeData.addresses.map((address: any, index: number) => {
    // Append postcode to the formatted_address array
    const formattedLines = [
      ...address.formatted_address.filter((line: string) => line.trim()),
      postcodeData.postcode
    ];

    const mainAddress = formattedLines[0] || address.line_1 || '';
    const secondaryAddress =
      formattedLines.slice(1).filter(Boolean).join(', ') ||
      [address.town_or_city, address.county].filter(Boolean).join(', ');

    const label =
      trimAddress({
        mainAddress,
        secondryAddress: secondaryAddress,
      })?.trim() || mainAddress;

    const description = formattedLines.join(', ') || address.line_1;

    const uniqueIdentifier =
      `${postcodeData.postcode}_${address.line_1}_${index}`.replace(
        /\s+/g,
        ''
      );
    const placeId = `postcode_${uniqueIdentifier}`;

    return {
      label: label,
      description: description,
      placeId: placeId,
      value: placeId,
      isPostcodeData: true,
      postcodeData: {
        postcode: postcodeData.postcode,
        latitude: postcodeData.latitude,
        longitude: postcodeData.longitude,
        address: address,
      },
    };
  });
};

export const processAddressSuggestions = (
  predictions: any[],
  postcodeData: any
): AddressOption[] => {
  const mappedOptions: AddressOption[] = predictions.map((item: any) => {
    const isCustomAddress =
      item?.structured_formatting?.main_text &&
      item?.structured_formatting?.secondary_text;
    const label = trimAddress({
      mainAddress: isCustomAddress
        ? item?.structured_formatting?.main_text
        : item?.description,
      secondryAddress: item?.structured_formatting?.secondary_text,
    })?.trim();

    return {
      label,
      description: item.description,
      placeId: item.place_id,
      value: item.place_id,
      structured_formatting: item.structured_formatting,
    };
  });

  // Handle postcode data - add addresses from postcodeData
  if (
    postcodeData &&
    postcodeData.addresses &&
    postcodeData.addresses.length > 0
  ) {
    const postcodeOptions = mapPostcodeDataToOptions(postcodeData);
    return [...mappedOptions, ...postcodeOptions];
  }

  return mappedOptions;
};

export const fetchAddressSuggestionsWithValidation = async (
  input: string,
): Promise<AddressOption[]> => {
  if (!input || input.length === 0) {
    return [];
  }

  if (input.length < 3) {
    return [];
  }

  const { predictions, postcodeData } = await fetchAddressSuggestions(input);
  return processAddressSuggestions(predictions, postcodeData);
};

export const buildPostcodeAddressComponents = (postcodeData: any, label: string) => {
  return {
    address_components: [
      {
        long_name: postcodeData.address.line_1,
        short_name: postcodeData.address.line_1,
        types: ['street_number', 'route'],
      },
      {
        long_name: postcodeData.address.thoroughfare,
        short_name: postcodeData.address.thoroughfare,
        types: ['route'],
      },
      {
        long_name: postcodeData.address.town_or_city,
        short_name: postcodeData.address.town_or_city,
        types: ['locality'],
      },
      {
        long_name: postcodeData.address.district,
        short_name: postcodeData.address.district,
        types: ['administrative_area_level_2'],
      },
      {
        long_name: postcodeData.postcode,
        short_name: postcodeData.postcode,
        types: ['postal_code'],
      },
      {
        long_name: postcodeData.address.country,
        short_name: postcodeData.address.country,
        types: ['country'],
      },
    ],
    formatted_address: label,
    geometry: {
      location: {
        lat: postcodeData.latitude,
        lng: postcodeData.longitude,
      },
    },
  };
};
